import Vue from 'vue';
import { Servicelayer } from '.';
import ServiceResponse from './responseTypes/serviceResponse';

type DateType = NonNullable<Parameters<typeof Vue.$date>[0]>;

export default (service: Servicelayer) => ({

  getFileName(response: ServiceResponse) {
    let filename;
    const disposition = response.raw.headers?.['content-disposition'];
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, '');
      }
    }
    return filename;
  },

  downloadIcs(fileName: string, eventStart: DateType, eventEnd: DateType, eventTitle: string, eventDescription?: string | null, eventLocation?: string | null) {
    const eventStartDate = Vue.$date(eventStart);
    const eventEndDate = Vue.$date(eventEnd);

    const isEventStartDateFullDay = eventStartDate.hour() === 0 && eventStartDate.minute() === 0 && eventStartDate.second() === 0;
    const isEventEndDateFullDay = eventEndDate.hour() === 0 && eventEndDate.minute() === 0 && eventEndDate.second() === 0;
    const isEventFullDay = isEventStartDateFullDay && isEventEndDateFullDay;
    let icsContent = '';
    icsContent += 'BEGIN:VCALENDAR\n';
    icsContent += 'VERSION:2.0\n';
    icsContent += 'BEGIN:VEVENT\n';
    if (isEventFullDay) {
      icsContent += `DTSTART:${eventStartDate.format('YYYYMMDD')}\n`;
      // Because the last day is not counted as a full day otherwise
      icsContent += `DTEND:${eventEndDate.add(1, 'day').format('YYYYMMDD')}\n`;
    } else {
      icsContent += `DTSTART:${eventStartDate.utc().format('YYYYMMDDTHHmmss')}Z\n`;
      icsContent += `DTEND:${eventEndDate.utc().format('YYYYMMDDTHHmmss')}Z\n`;
    }
    icsContent += `SUMMARY:${eventTitle}\n`;
    if (eventDescription) {
      icsContent += `DESCRIPTION:${eventDescription.replace(/(?:\r\n|\r|\n)/g, '\\n')}\n`;
    }
    if (eventLocation) {
      icsContent += `LOCATION:${eventLocation.replace(/(?:\r\n|\r|\n)/g, '\\n')}\n`;
    }
    icsContent += 'END:VEVENT\n';
    icsContent += 'END:VCALENDAR\n';
    fileName = fileName ?? 'event.ics';
    fileName = fileName.includes('.ics') ? fileName : `${fileName}.ics`;
    this.downloadFileRaw(fileName, icsContent);
  },

  downloadFileRaw(fileName: string, rawData: any) {
    const blob = new Blob([rawData], { type: 'application/octet-stream' });
    return this.downloadFile(fileName, blob);
  },

  downloadFile(fileName: string, blob: Blob | File) {
    if (typeof (window.navigator as any).msSaveBlob !== 'undefined') {
      // IE workaround for "HTML7007: One or more blob URLs were
      // revoked by closing the blob for which they were created.
      // These URLs will no longer resolve as the data backing
      // the URL has been freed."
      (window.navigator as any).msSaveBlob(blob, fileName);
    } else {
      const blobURL = window.URL.createObjectURL(blob);
      const tempLink = document.createElement('a');
      tempLink.style.display = 'none';
      tempLink.href = blobURL;
      tempLink.setAttribute('download', fileName);

      // Safari thinks _blank anchor are pop ups. We only want to set _blank
      // target if the browser does not support the HTML5 download attribute.
      // This allows you to download files in desktop safari if pop up blocking
      // is enabled.
      if (typeof tempLink.download === 'undefined') {
        tempLink.setAttribute('target', '_blank');
      }

      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(blobURL);
    }
  },
});
